import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ObtenerPedidoSuccess } from "../../../services/store/compras";
import "./CheckoutPaymentPaggo.css";

const PAGGO_POLL_MS = 1000;
const PAGGO_POLL_MAX = 180;

export default function CheckoutPaymentPaggo({ initialData }) {
  const navigate = useNavigate();

  const [status] = useState("ready");
  const [confirmPhase, setConfirmPhase] = useState("polling");
  const [opened, setOpened] = useState(false);

  const paymentUrl = initialData?.payment_url || initialData?.link || "";
  const grupoRef = useRef(
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("grupo_reserva")
      : null
  );

  const autoOpenKeyRef = useRef(
    grupoRef.current ? `paggo_auto_open_${grupoRef.current}` : "paggo_auto_open"
  );

  const autoOpenExecutedRef = useRef(false);

  useEffect(() => {
    if (!paymentUrl) return;
    if (autoOpenExecutedRef.current) return;

    autoOpenExecutedRef.current = true;

    const storageKey = autoOpenKeyRef.current;

    try {
      const alreadyOpened = sessionStorage.getItem(storageKey);
      if (alreadyOpened === "1") {
        setOpened(true);
        return;
      }

      sessionStorage.setItem(storageKey, "1");
    } catch {}

    const newWindow = window.open(paymentUrl, "_blank", "noopener,noreferrer");
    const wasOpened = !!newWindow;

    setOpened(wasOpened);

    if (!wasOpened) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {}
    }
  }, [paymentUrl]);

  useEffect(() => {
    if (confirmPhase !== "polling" || !grupoRef.current || status !== "ready") {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timerId = null;
    const grupo = grupoRef.current;

    const schedule = (delay) => {
      timerId = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled) return;

      try {
        const data = await ObtenerPedidoSuccess({ grupo_reserva: grupo });

        if (cancelled) return;

        const pedidoId = data?.pedido_id;
        const estadoPago = String(data?.estado_pago || "").toUpperCase();

        if (pedidoId != null && estadoPago === "PAGADO") {
          try {
            localStorage.removeItem("cart");
            localStorage.removeItem("cart_expiration");
            localStorage.removeItem("art_expiration");
          } catch {}

          try {
            sessionStorage.setItem("pedido_id", String(pedidoId));
          } catch {}

          navigate(
            `/store/checkout/success?gateway=paggo&pedido_id=${encodeURIComponent(
              String(pedidoId)
            )}`,
            {
              replace: true,
              state: { no_po: data?.no_po ? String(data.no_po) : null },
            }
          );
          return;
        }
      } catch {}

      attempts += 1;

      if (attempts >= PAGGO_POLL_MAX) {
        setConfirmPhase("timeout");
        return;
      }

      schedule(PAGGO_POLL_MS);
    };

    schedule(0);

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [confirmPhase, navigate, status]);

  const handleOpenAgain = () => {
    if (!paymentUrl) return;
    const newWindow = window.open(paymentUrl, "_blank", "noopener,noreferrer");
    if (newWindow) setOpened(true);
  };

  const handleGoOrders = () => {
    navigate("/cliente/orders", {
      state: { mode: "login", redirectTo: "/cliente/login" },
    });
  };

  const handleBackCart = () => {
    navigate("/store/cart");
  };

  if (!paymentUrl) {
    return (
      <section className="cyravi-pay-page">
        <div className="cyravi-pay-glow cyravi-pay-glow--top" />
        <div className="cyravi-pay-glow cyravi-pay-glow--left" />
        <div className="cyravi-pay-glow cyravi-pay-glow--right" />
        <div className="cyravi-pay-grid" />

        <div className="cyravi-pay-shell">
          <div className="cyravi-pay-card cyravi-pay-card--error">
            <div className="cyravi-pay-card-topline" />
            <div className="cyravi-pay-badge cyravi-pay-badge--danger">
              Error de pago
            </div>

            <div className="cyravi-pay-icon cyravi-pay-icon--danger">!</div>

            <h1 className="cyravi-pay-title">No se pudo abrir el link de pago</h1>
            <p className="cyravi-pay-text">
              No recibimos el enlace de pago. Puedes volver al carrito e intentar
              nuevamente.
            </p>

            <div className="cyravi-pay-actions">
              <button
                type="button"
                className="cyravi-pay-btn cyravi-pay-btn--primary"
                onClick={handleBackCart}
              >
                Volver al carrito
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (confirmPhase === "timeout") {
    return (
      <section className="cyravi-pay-page">
        <div className="cyravi-pay-glow cyravi-pay-glow--top" />
        <div className="cyravi-pay-glow cyravi-pay-glow--left" />
        <div className="cyravi-pay-glow cyravi-pay-glow--right" />
        <div className="cyravi-pay-grid" />

        <div className="cyravi-pay-shell">
          <div className="cyravi-pay-card">
            <div className="cyravi-pay-card-topline" />
            <div className="cyravi-pay-badge">cyravi</div>

            <div className="cyravi-pay-status-row">
              <span className="cyravi-pay-pill cyravi-pay-pill--warning">
                Confirmación pendiente
              </span>
            </div>

            <h1 className="cyravi-pay-title">Seguimos esperando la confirmación</h1>

            <p className="cyravi-pay-text">
              No recibimos confirmación en este momento. Si ya pagaste,
              tu pedido puede reflejarse en estado no pagado en
              <strong> Mis pedidos</strong>.
            </p>

            <div className="cyravi-pay-info-box">
              <div className="cyravi-pay-info-title">¿Qué puedes hacer ahora?</div>
              <ul className="cyravi-pay-list">
                <li>Revisar si el pago ya fue completado.</li>
                <li>Entrar a tus pedidos para validar el estado.</li>
                <li>Volver a abrir el enlace si todavía no finalizaste el cobro.</li>
              </ul>
            </div>

            <div className="cyravi-pay-actions">
              <button
                type="button"
                className="cyravi-pay-btn cyravi-pay-btn--primary"
                onClick={handleGoOrders}
              >
                Ver mis pedidos
              </button>

              <button
                type="button"
                className="cyravi-pay-btn cyravi-pay-btn--ghost"
                onClick={handleOpenAgain}
              >
                Abrir Paggo de nuevo
              </button>

              <button
                type="button"
                className="cyravi-pay-btn cyravi-pay-btn--ghost"
                onClick={handleBackCart}
              >
                Volver al carrito
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cyravi-pay-page">
      <div className="cyravi-pay-glow cyravi-pay-glow--top" />
      <div className="cyravi-pay-glow cyravi-pay-glow--left" />
      <div className="cyravi-pay-glow cyravi-pay-glow--right" />
      <div className="cyravi-pay-grid" />

      <div className="cyravi-pay-shell">
        <div className="cyravi-pay-card">
          <div className="cyravi-pay-card-topline" />
          <div className="cyravi-pay-badge">cyravi</div>

          <div className="cyravi-pay-hero">
            <div className="cyravi-pay-spinner" />
          </div>

          <div className="cyravi-pay-status-row">
            <span className="cyravi-pay-pill cyravi-pay-pill--active">
              Confirmando pago
            </span>
          </div>

          <h1 className="cyravi-pay-title">Estamos validando tu pago</h1>

          <p className="cyravi-pay-text">
            Completa el cobro en el link de pago si aún no lo has hecho. Esta página se
            actualizará automáticamente cuando el pago sea confirmado.
          </p>

          <div className="cyravi-pay-steps">
            <div className="cyravi-pay-step cyravi-pay-step--done">
              <span className="cyravi-pay-step-dot" />
              Enlace generado
            </div>
            <div className="cyravi-pay-step cyravi-pay-step--active">
              <span className="cyravi-pay-step-dot" />
              Esperando confirmación
            </div>
            <div className="cyravi-pay-step">
              <span className="cyravi-pay-step-dot" />
              Redirección a pedido exitoso
            </div>
          </div>

          <div className="cyravi-pay-info-box">
            <div className="cyravi-pay-info-title">Estado de la pestaña de pago</div>
            <p className="cyravi-pay-info-text">
              {opened
                ? "El link de pago fue abierto en otra pestaña. Puedes completar el pago allí mientras esta pantalla espera la confirmación."
                : "Tu navegador bloqueó la apertura automática. Usa el botón de abajo para abrir el link de pago manualmente."}
            </p>
          </div>

          <div className="cyravi-pay-actions">
            <button
              type="button"
              className="cyravi-pay-btn cyravi-pay-btn--primary"
              onClick={handleOpenAgain}
            >
              Abrir link de pago de nuevo
            </button>

            <button
              type="button"
              className="cyravi-pay-btn cyravi-pay-btn--ghost"
              onClick={handleBackCart}
            >
              Volver al carrito
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}